/**
 * When a browser-decoded tile must switch to the server-composed picture.
 *
 * A DJI tile plays through Agora and an HLS tile through its own player, neither
 * of which passes through our compositor — so an AI layer can only be *seen* on
 * them by swapping the tile over to the stream the backend composites. That swap
 * is decided in three places (the token fetch in App.tsx, and `useServerProcessing`
 * / `useWebRTC` in VideoRenderer), and they must agree: fetch without switching and
 * the tile shows Agora while a publisher runs for nobody; switch without fetching
 * and the tile falls back to MJPEG for no reason.
 *
 * Detection was missing from all three, which is why road segmentation worked on
 * the drone and object detection did not — the tile stayed on Agora, so the model's
 * output was never in the picture. `DETECT` that quietly does not detect is the same
 * class of bug as `DETECT` never saying which model it ran.
 */
export function needsServerProcessing(
  roadSegEnabled?: boolean,
  geoMaskEnabled?: boolean,
  detectionEnabled?: boolean,
): boolean {
  // Optional because a grid cell's detection flag is derived per feed and can be
  // undefined before the channel state has loaded. Undefined means "not on" —
  // the fail-safe direction, since guessing "on" would swap a tile away from its
  // own player on the strength of state we do not have yet.
  return !!roadSegEnabled || !!geoMaskEnabled || !!detectionEnabled;
}

/**
 * Whether a DJI tile shows the server's picture rather than its own HLS player.
 *
 * A SEPARATE question from `needsServerProcessing` above, and deliberately not a
 * fourth argument to it. That one is twinned with `processing_source.
 * primary_ai_active()` on the server, which decides whether the processing
 * reader may be reaped — so it has to keep meaning exactly "an AI layer is
 * consuming these frames" and nothing else. This is a deployment's transport
 * preference, which consumes nothing and reaps nothing.
 *
 * Why it exists: an aircraft tile is the one feed played as HLS, and HLS here is
 * seconds behind — nginx-rtmp cuts a fragment only on a keyframe, so a fragment
 * is the aircraft's ~2 s GOP whatever `hls_fragment` asks for, and hls.js sits
 * `liveSyncDurationCount` of those further back again. The same aircraft over
 * the server-read path is 162 ms (MJPEG) or 271 ms (WebRTC), measured.
 *
 * `hasDeviceSn` is a requirement, not a formality. The swapped tile addresses
 * the backend by SERIAL — that is what resolves to the reader already pulling
 * that aircraft, instead of "the rtmp feed", which the backend resolves to
 * whatever the PROCESSING source happens to be. Without a serial the swap would
 * put another camera's picture under this aircraft's name, so no serial means
 * stay on HLS: seconds late is a far smaller failure than wrong.
 *
 * `lowLatency` comes from the server (`GET /api/drone/overlays`), never from a
 * build-time constant — the client cannot derive it from an `.m3u8`, and the
 * dashboard polls that endpoint precisely so it never assumes.
 */
export function prefersServerPicture(opts: {
  /** Road seg, geo mask or detection is running — the historical reason to swap. */
  aiLayerActive: boolean;
  /** This tile knows which aircraft it is showing. */
  hasDeviceSn: boolean;
  /** This deployment set DJI_LOW_LATENCY. Undefined before the first poll lands. */
  lowLatency?: boolean;
}): boolean {
  if (opts.aiLayerActive) return true;
  return !!opts.lowLatency && opts.hasDeviceSn;
}

/**
 * Whether the video panel may take the PRIMARY room's WebRTC picture instead of
 * opening an MJPEG connection.
 *
 * A different question from `needsServerProcessing` above, which asks whether a
 * *browser-decoded* tile must abandon its own player to show an AI layer. This
 * one is about transport: MJPEG re-sends every pixel of every frame — measured
 * ~6.5 Mbit/s for one 640x360 tile at quality 75 — while the same picture over
 * WebRTC is ~0.6 and the browser decodes it in hardware. The panel used to pay
 * the MJPEG price whenever no model was running, which is most of the time.
 *
 * **`matchesProcessingFeed` is not a formality.** The primary room carries the
 * processing feed and nothing else, so subscribing to it while the panel shows a
 * different camera would put the wrong picture on screen under the right
 * camera's name. `activateSource()` posts `/api/drone/video-source` for every
 * server-read source, so the docked panel's feed *is* the processing one in
 * practice — but that is an invariant of a call site thousands of lines away,
 * and the failure it guards against is an operator acting on the wrong camera.
 * So it is checked against what the server says it is publishing.
 *
 * The `aiLayerActive` arm is the historical rule, unchanged: a layer forces the
 * swap on its own, which is what a DJI or HLS tile needs in order to show boxes
 * at all.
 *
 * False means MJPEG — never a blank tile, the same contract as `useFeedWebrtc`
 * returning null.
 */
export function prefersPrimaryWebrtc(opts: {
  /** A subscribe-only token for the primary room exists. */
  hasCreds: boolean;
  /** Road seg, geo mask or detection is running on the primary. */
  aiLayerActive: boolean;
  /** The server says the feed it is publishing is the one this panel shows. */
  matchesProcessingFeed: boolean;
}): boolean {
  return opts.hasCreds && (opts.aiLayerActive || opts.matchesProcessingFeed);
}
